import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';

import { websiteApi } from './websiteApi';

const POLL_MS = 3000;

/**
 * Photo upload and status. Each photo is classified in the background (role, usable,
 * plates blurred, person visible); rejected ones show the reason, a visible person needs the
 * owner's confirmation before it can be published.
 */
export default function ImagesPanel({ orgId, onChanged, notify }) {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await websiteApi.images(orgId);
      setImages(data);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const pending = images.some((i) => i.status === 'pending' || i.status === 'processing');
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(async () => {
      await load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [pending, load]);

  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) onChanged?.();
    wasPending.current = pending;
  }, [pending, onChanged]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        await websiteApi.uploadImage(orgId, file);
      }
      await load();
    } catch (err) {
      const code = err?.response?.data?.code;
      notify?.(t(code === 'too_many_images' ? 'website.images.too_many' : 'website.images.upload_failed'), 'error');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (image) => {
    try {
      await websiteApi.deleteImage(orgId, image.id);
      await load();
      onChanged?.();
    } catch {
      notify?.(t('website.action_failed'), 'error');
    }
  };

  const confirmPerson = async (image) => {
    try {
      await websiteApi.patchImage(orgId, image.id, { person_confirmed: true });
      await load();
      onChanged?.();
    } catch {
      notify?.(t('website.action_failed'), 'error');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;

  return (
    <Box data-testid="website-images">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('website.images.guide')}</Typography>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => e.target.files?.length && upload(Array.from(e.target.files))}
        data-testid="website-image-input"
      />
      <Button variant="outlined" onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ mb: 2 }} data-testid="website-image-upload">
        {uploading ? t('website.images.uploading') : t('website.images.add')}
      </Button>

      <Stack spacing={1.5}>
        {images.map((img) => (
          <Box key={img.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }} data-testid={`website-image-${img.id}`}>
            <Box sx={{ width: 96, height: 72, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover', flexShrink: 0 }}>
              {img.thumb_url && <img src={img.thumb_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Chip size="small" label={t(`website.images.role.${img.role}`, { defaultValue: img.role })} />
                <Chip
                  size="small"
                  color={img.status === 'ready' ? 'success' : img.status === 'rejected' ? 'error' : 'default'}
                  label={t(`website.images.status.${img.status}`)}
                />
                {img.contains_person && !img.person_confirmed && <Chip size="small" color="warning" label={t('website.images.person_pending')} />}
              </Stack>
              {img.reject_reason && <Typography variant="caption" color="error">{img.reject_reason}</Typography>}
            </Box>
            {img.contains_person && !img.person_confirmed && img.status === 'ready' && (
              <Tooltip title={t('website.images.confirm_person')}>
                <IconButton size="small" onClick={() => confirmPerson(img)} data-testid={`confirm-person-${img.id}`}><CheckCircleOutlineIcon /></IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => remove(img)} data-testid={`delete-image-${img.id}`}><DeleteOutlineIcon /></IconButton>
          </Box>
        ))}
        {images.length === 0 && <Typography variant="body2" color="text.secondary">{t('website.images.none')}</Typography>}
      </Stack>
    </Box>
  );
}
